import React, { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { Mail, MapPin, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission for now
      // In a real implementation, you would connect to Resend API
      console.log('Form data submitted:', formData);
      
      // Success toast
      toast({
        title: "Message sent!",
        description: "Thanks for reaching out. I'll get back to you soon."
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Something went wrong",
        description: "Could not send your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="text-grass" />,
      label: 'Email',
      value: 'alex@pixeldev.example'
    }, 
    {
      icon: <MapPin className="text-grass" />,
      label: 'Location',
      value: 'Pixel Valley, Digital World'
    }, 
    {
      icon: <Clock className="text-grass" />,
      label: 'Availability',
      value: 'Monday-Friday, 9am-5pm'
    }
  ];

  return (
    <section id="contact" className="py-20 relative">
      <div className="section-container">
        <h2 className="section-title text-forest">Get in Touch</h2>
        <div className="grid md:grid-cols-2 gap-10 mt-12">
          {/* Contact Info */}
          <div className="bg-white/10 backdrop-blur-md border-2 border-grass/30 p-8 shadow-[6px_6px_0px_0px_rgba(30,58,43,0.3)]">
            <div className="space-y-6 mb-8">
              <h3 className="font-degular text-2xl text-grass-light">Let's Connect</h3>
              <p className="font-mono text-white/90 leading-relaxed">
                Have a project in mind or just want to say hello? Fill out the form or reach me through the contact details below.
              </p>
            </div>
            
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="mt-1 h-12 w-12 rounded-none bg-forest/40 border-2 border-grass/50 flex items-center justify-center transition-all group-hover:bg-grass/20">
                    {info.icon}
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-grass-light font-degular">{info.label}</h4>
                    <p className="font-mono text-white/80">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Contact Form - Styled like review boxes */}
          <div className="bg-white/10 backdrop-blur-md border-2 border-grass/30 p-8 shadow-[6px_6px_0px_0px_rgba(30,58,43,0.3)]">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-degular text-grass-light">
                    Your Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="border-2 border-grass/50 focus:border-grass bg-forest/30 text-white placeholder:text-white/50 shadow-[2px_2px_0px_0px_rgba(30,58,43,0.3)]"
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-degular text-grass-light">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="border-2 border-grass/50 focus:border-grass bg-forest/30 text-white placeholder:text-white/50 shadow-[2px_2px_0px_0px_rgba(30,58,43,0.3)]"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject" className="font-degular text-grass-light">
                  Subject
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="border-2 border-grass/50 focus:border-grass bg-forest/30 text-white placeholder:text-white/50 shadow-[2px_2px_0px_0px_rgba(30,58,43,0.3)]"
                  placeholder="Project inquiry"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message" className="font-degular text-grass-light">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="min-h-[120px] border-2 border-grass/50 focus:border-grass bg-forest/30 text-white placeholder:text-white/50 shadow-[2px_2px_0px_0px_rgba(30,58,43,0.3)]"
                  placeholder="Tell me about your project..."
                />
              </div>
              
              <Button 
                type="submit" 
                disabled={isSubmitting} 
                className="w-full bg-grass hover:bg-grass-light text-white font-degular py-3 px-4 border-2 border-forest shadow-[4px_4px_0px_0px_rgba(30,58,43,0.6)] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_0px_rgba(30,58,43,0.7)] transition-all"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;